/**
 * TeacherPresetPanel - プリセット管理モーダル
 * 教師モードで認知特性 + 呈現モード偏好プリセットを管理する
 *
 * ICCHP 2026 §6.5: Felder-Silverman 4 軸の固定タイプ分類セレクタは廃止されました。
 * 代わりに「呈現モード偏好（叙述/ステップ/アイコン優先度）」のスライダーを使用します。
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { teacherAPI } from '../utils/api';
import { logClientError } from '../utils/safeLog';

// ICCHP §6.5: 旧 LEARNING_STYLE_AXES (4 軸固定タイプセレクタ) は廃止されました。
// 新規 UI: 呈現モード偏好を 3 モード（叙述/ステップ/アイコン）の優先度スライダーで設定。
const PRESENTATION_PREFERENCE_SLIDERS = [
  { key: 'pref_narrative', label: '叙述（narrative）優先度', desc: '文章による説明を好む生徒' },
  { key: 'pref_step_by_step', label: 'ステップ（step-by-step）優先度', desc: '段階的展開を好む生徒' },
  { key: 'pref_iconographic', label: 'アイコン（iconographic）優先度', desc: '視覚要素を好む生徒' },
];

const COGNITIVE_SLIDERS = [
  { key: 'working_memory', label: 'ワーキングメモリ' },
  { key: 'visuospatial', label: '視空間処理' },
  { key: 'crossmodal', label: 'クロスモーダル' },
  { key: 'load_vulnerability', label: '認知負荷脆弱性' },
  { key: 'executive_inhibition', label: '実行機能・抑制' },
  { key: 'language_semantic', label: '言語意味処理' },
];

const MODIFIER_SLIDERS = [
  { key: 'math_anxiety', label: '数学不安' },
  { key: 'graph_literacy', label: 'グラフリテラシー' },
];

const EMPTY_FORM = {
  name: '',
  // ICCHP §6.5: 呈現モード偏好スライダー（0-10 整数値で入力、softmax で正規化して保存）
  pref_narrative: 5,
  pref_step_by_step: 5,
  pref_iconographic: 5,
  working_memory: 0,
  visuospatial: 0,
  crossmodal: 0,
  load_vulnerability: 0,
  executive_inhibition: 0,
  language_semantic: 0,
  math_anxiety: 0,
  graph_literacy: 0,
  support_level: 2,
};

// 3 つのスライダー値を softmax 確率に正規化
function _normalizePresentationPreference(form) {
  const raw = {
    narrative: Number(form.pref_narrative ?? 5),
    step_by_step: Number(form.pref_step_by_step ?? 5),
    iconographic: Number(form.pref_iconographic ?? 5),
  };
  const total = raw.narrative + raw.step_by_step + raw.iconographic;
  if (total <= 0) {
    return { narrative: 1 / 3, step_by_step: 1 / 3, iconographic: 1 / 3 };
  }
  return {
    narrative: raw.narrative / total,
    step_by_step: raw.step_by_step / total,
    iconographic: raw.iconographic / total,
  };
}

function TeacherPresetPanel({ isOpen, onClose, onPresetChange }) {
  const { t } = useTranslation();
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');

  const loadPresets = useCallback(async () => {
    setLoading(true);
    try {
      const response = await teacherAPI.getPresets();
      setPresets(response.data || []);
    } catch (err) {
      logClientError('Failed to load presets:', err);
      setError('preset.loadFailed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadPresets();
      setError('');
    }
  }, [isOpen, loadPresets]);

  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleEdit = (preset) => {
    setEditingPreset(preset);
    // ICCHP §6.5: 呈現モード偏好を softmax から 0-10 整数値に逆算
    // (legacy preset では presentation_preference が null の場合があるため、
    //  デフォルト値 5 で初期化)
    const pp = preset.presentation_preference || null;
    let narrativePref = 5, stepPref = 5, iconPref = 5;
    if (pp) {
      // softmax 確率を 0-10 スケールに変換（10 でスケーリング）
      const scale = 30; // 0-10 になるように
      narrativePref = Math.round((pp.narrative ?? 1 / 3) * scale / 3 * 10) / 10;
      stepPref = Math.round((pp.step_by_step ?? 1 / 3) * scale / 3 * 10) / 10;
      iconPref = Math.round((pp.iconographic ?? 1 / 3) * scale / 3 * 10) / 10;
      // クリップ
      narrativePref = Math.min(10, Math.max(0, narrativePref));
      stepPref = Math.min(10, Math.max(0, stepPref));
      iconPref = Math.min(10, Math.max(0, iconPref));
    }
    setFormData({
      name: preset.name || '',
      pref_narrative: narrativePref,
      pref_step_by_step: stepPref,
      pref_iconographic: iconPref,
      working_memory: preset.cognitive_working_memory || 0,
      visuospatial: preset.cognitive_visuospatial || 0,
      crossmodal: preset.cognitive_crossmodal || 0,
      load_vulnerability: preset.cognitive_load_vulnerability || 0,
      executive_inhibition: preset.cognitive_executive_inhibition || 0,
      language_semantic: preset.cognitive_language_semantic || 0,
      math_anxiety: preset.cognitive_math_anxiety || 0,
      graph_literacy: preset.cognitive_graph_literacy || 0,
      support_level: preset.support_level || 2,
    });
    setShowForm(true);
  };

  const handleNew = () => {
    setEditingPreset(null);
    setFormData({ ...EMPTY_FORM });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingPreset(null);
    setError('');
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('preset.nameRequired');
      return;
    }
    setError('');

    // ICCHP §6.5: 旧 learning_style_* 4 列は payload に含めない（NULL 強制）
    // 代わりに presentation_preference (softmax 正規化済み) を保存
    const payload = {
      name: formData.name.trim(),
      presentation_preference: _normalizePresentationPreference(formData),
      cognitive_working_memory: formData.working_memory,
      cognitive_visuospatial: formData.visuospatial,
      cognitive_crossmodal: formData.crossmodal,
      cognitive_load_vulnerability: formData.load_vulnerability,
      cognitive_executive_inhibition: formData.executive_inhibition,
      cognitive_language_semantic: formData.language_semantic,
      cognitive_math_anxiety: formData.math_anxiety,
      cognitive_graph_literacy: formData.graph_literacy,
      support_level: formData.support_level,
    };

    try {
      if (editingPreset) {
        await teacherAPI.updatePreset(editingPreset.id, payload);
      } else {
        await teacherAPI.createPreset(payload);
      }
      await loadPresets();
      setShowForm(false);
      setEditingPreset(null);
      if (onPresetChange) onPresetChange();
    } catch (err) {
      logClientError('Failed to save preset:', err);
      setError('preset.saveFailed');
    }
  };

  const handleDelete = async (presetId) => {
    if (!window.confirm(t('preset.deleteConfirm'))) return;
    try {
      await teacherAPI.deletePreset(presetId);
      await loadPresets();
      if (editingPreset?.id === presetId) {
        setShowForm(false);
        setEditingPreset(null);
      }
      if (onPresetChange) onPresetChange();
    } catch (err) {
      logClientError('Failed to delete preset:', err);
      setError('preset.deleteFailed');
    }
  };

  const handleSetDefault = async (presetId) => {
    try {
      await teacherAPI.setDefaultPreset(presetId);
      await loadPresets();
      if (onPresetChange) onPresetChange();
    } catch (err) {
      logClientError('Failed to set default preset:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm px-4"
      style={{ backgroundColor: 'rgba(30,45,56,0.4)' }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="teacher-preset-title"
        className="w-full max-w-lg rounded-[28px] border p-6 max-h-[90vh] overflow-y-auto"
        style={{
          borderColor: 'var(--border)',
          backgroundColor: 'var(--bg-surface)',
          boxShadow: '0 24px 60px rgba(61,90,110,0.15)',
        }}
      >
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <h2 id="teacher-preset-title" className="text-2xl font-bold" style={{ color: 'var(--primary)' }}>
            {t('teacher.managePresets')}
          </h2>
          <button
            onClick={onClose}
            className="text-3xl font-bold transition-colors"
            aria-label={t('common.close')}
            style={{ color: 'var(--text-muted)' }}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--danger)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-muted)')}
          >
            x
          </button>
        </div>

        {error && (
          <div
            role="alert"
            className="mb-4 rounded-2xl p-3 text-sm"
            style={{ border: '1px solid var(--danger)', background: 'var(--danger-bg)', color: 'var(--danger)' }}
          >
            {t(error)}
          </div>
        )}

        {!showForm ? (
          <>
            {/* Preset list */}
            {loading ? (
              <p className="text-center py-8" style={{ color: 'var(--text-muted)' }}>{t('common.loading')}</p>
            ) : presets.length === 0 ? (
              <p className="text-center py-8" style={{ color: 'var(--text-muted)' }}>{t('preset.noPresets')}</p>
            ) : (
              <div className="space-y-3 mb-6">
                {presets.map((preset) => (
                  <div
                    key={preset.id}
                    className="flex items-center justify-between rounded-2xl border p-4"
                    style={{ borderColor: 'var(--border)', background: 'var(--bg-base)' }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm" style={{ color: 'var(--text)' }}>
                          {preset.name}
                        </span>
                        {preset.is_default && (
                          <span
                            className="rounded-full px-2 py-0.5 text-xs font-semibold"
                            style={{ background: 'var(--accent)', color: '#fff' }}
                          >
                            {t('preset.defaultBadge')}
                          </span>
                        )}
                      </div>
                      <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                        {t('preset.supportLevelShort', { level: preset.support_level || '?' })}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      {!preset.is_default && (
                        <button
                          onClick={() => handleSetDefault(preset.id)}
                          className="rounded-lg px-2 py-1 text-xs font-semibold transition-colors"
                          style={{ color: 'var(--accent)', border: '1px solid var(--accent)' }}
                        >
                          {t('preset.setDefault')}
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(preset)}
                        className="rounded-lg px-2 py-1 text-xs font-semibold transition-colors"
                        style={{ color: 'var(--primary)', border: '1px solid var(--primary)' }}
                      >
                        {t('preset.edit')}
                      </button>
                      <button
                        onClick={() => handleDelete(preset.id)}
                        className="rounded-lg px-2 py-1 text-xs font-semibold transition-colors"
                        style={{ color: 'var(--danger)', border: '1px solid var(--danger)' }}
                      >
                        {t('preset.delete')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={handleNew}
              className="w-full rounded-full py-3 text-sm font-semibold transition-transform hover:scale-[1.02]"
              style={{ background: 'var(--primary)', color: 'var(--text-on-primary)' }}
            >
              + {t('preset.newPreset')}
            </button>
          </>
        ) : (
          <>
            {/* Preset form */}
            <div className="space-y-5">
              {/* Name */}
              <div>
                <label htmlFor="preset-name-input" className="mb-1 block text-sm font-semibold" style={{ color: 'var(--text)' }}>
                  {t('preset.nameLabel')}
                </label>
                <input
                  id="preset-name-input"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="input-field rounded-2xl"
                  placeholder={t('preset.namePlaceholder')}
                />
              </div>

              {/* ICCHP §6.5: 呈現モード偏好（叙述/ステップ/アイコン優先度スライダー） */}
              <div className="border-t pt-4" style={{ borderColor: 'var(--border)' }}>
                <h3 className="text-sm font-bold mb-2" style={{ color: 'var(--text)' }}>
                  {t('preset.ppSectionTitle')}
                </h3>
                <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
                  {t('preset.ppSectionNote')}
                </p>
                {PRESENTATION_PREFERENCE_SLIDERS.map((slider) => (
                  <div key={slider.key} className="mb-3">
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>
                        {t(`preset.slider.${slider.key}`)}
                      </label>
                      <span className="text-xs font-bold" style={{ color: 'var(--primary)' }}>
                        {formData[slider.key]}
                      </span>
                    </div>
                    <input
                      type="range"
                      aria-label={t(`preset.slider.${slider.key}`)}
                      min="0"
                      max="10"
                      step="1"
                      value={formData[slider.key]}
                      onChange={(e) => handleChange(slider.key, parseInt(e.target.value, 10))}
                      className="w-full"
                      style={{ accentColor: 'var(--primary)' }}
                    />
                    <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                      {t(`preset.sliderDesc.${slider.key}`)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Cognitive difficulty sliders */}
              <div className="border-t pt-4" style={{ borderColor: 'var(--border)' }}>
                <h3 className="text-sm font-bold mb-3" style={{ color: 'var(--text)' }}>
                  {t('preset.cognitiveSectionTitle')}
                </h3>
                {COGNITIVE_SLIDERS.map((slider) => (
                  <div key={slider.key} className="mb-3">
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>
                        {t(`preset.slider.${slider.key}`)}
                      </label>
                      <span className="text-xs font-bold" style={{ color: 'var(--primary)' }}>
                        {formData[slider.key]}
                      </span>
                    </div>
                    <input
                      type="range"
                      aria-label={t(`preset.slider.${slider.key}`)}
                      min="0"
                      max="3"
                      step="1"
                      value={formData[slider.key]}
                      onChange={(e) => handleChange(slider.key, parseInt(e.target.value, 10))}
                      className="w-full"
                      style={{ accentColor: 'var(--primary)' }}
                    />
                  </div>
                ))}

                {/* Modifier sliders */}
                <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
                  <p className="text-xs font-bold mb-3" style={{ color: 'var(--text-muted)' }}>{t('preset.modifierSectionTitle')}</p>
                  {MODIFIER_SLIDERS.map((slider) => (
                    <div key={slider.key} className="mb-3">
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>
                          {t(`preset.slider.${slider.key}`)}
                        </label>
                        <span className="text-xs font-bold" style={{ color: 'var(--accent)' }}>
                          {formData[slider.key]}
                        </span>
                      </div>
                      <input
                        type="range"
                        aria-label={t(`preset.slider.${slider.key}`)}
                        min="0"
                        max="3"
                        step="1"
                        value={formData[slider.key]}
                        onChange={(e) => handleChange(slider.key, parseInt(e.target.value, 10))}
                        className="w-full"
                        style={{ accentColor: 'var(--accent)' }}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Support level */}
              <div className="border-t pt-4" style={{ borderColor: 'var(--border)' }}>
                <span id="preset-support-level-label" className="block text-sm font-bold mb-2" style={{ color: 'var(--text)' }}>
                  {t('preset.supportLevelLabel')}
                </span>
                <div className="flex gap-4" role="radiogroup" aria-labelledby="preset-support-level-label">
                  {[1, 2, 3].map((level) => (
                    <label
                      key={level}
                      className="flex items-center gap-1.5 text-sm cursor-pointer"
                      style={{ color: 'var(--text)' }}
                    >
                      <input
                        type="radio"
                        name="preset-support-level"
                        value={level}
                        checked={formData.support_level === level}
                        onChange={() => handleChange('support_level', level)}
                      />
                      Lv.{level}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Form actions */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleCancel}
                className="flex-1 rounded-full py-3 text-sm font-semibold border transition-colors"
                style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSave}
                className="flex-1 rounded-full py-3 text-sm font-semibold transition-transform hover:scale-[1.02]"
                style={{ background: 'var(--primary)', color: 'var(--text-on-primary)' }}
              >
                {editingPreset ? t('preset.update') : t('common.save')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default TeacherPresetPanel;
